// Ordered multi-select for fulfilment warehouses.
//
// A vendor sub-account can be fulfilled from several warehouses; the
// SELECTION ORDER is the priority ranking (index 0 = primary). Picking
// a warehouse appends it; unpicking removes it and the rest keep their
// relative order. The summary shows the count and the primary.
//
// Implemented as a <details> popover (no external dropdown dep; mirrors
// the collapsible chrome already used across the integrations admin).
// Closes on outside-click and Escape so it behaves like a real menu.

import { useEffect, useRef } from "react";
import { Check, ChevronDown, Warehouse } from "lucide-react";
import { useTranslation } from "../../i18n";

interface Props {
    options: { warehouse: string; country?: string | null }[];
    selected: string[];
    onChange: (next: string[]) => void;
    disabled?: boolean;
}

const WarehouseMultiSelect = ({ options, selected, onChange, disabled = false }: Props) => {
    const { t } = useTranslation();
    const ref = useRef<HTMLDetailsElement>(null);

    // Close on outside-click / Escape, like a native menu.
    useEffect(() => {
        const onDocClick = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                ref.current.removeAttribute("open");
            }
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape" && ref.current) ref.current.removeAttribute("open");
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, []);

    const toggle = (warehouse: string) => {
        if (disabled) return;
        if (selected.includes(warehouse)) {
            onChange(selected.filter((w) => w !== warehouse));
        } else {
            // Append → newest pick is lowest priority; first pick stays primary.
            onChange([...selected, warehouse]);
        }
    };

    const summary =
        selected.length === 0
            ? t("integrations_warehouse_vendor_unset")
            : selected.length === 1
                ? selected[0]
                : t("integrations_vendor_warehouses_summary", {
                    count: String(selected.length),
                    primary: selected[0],
                });

    return (
        <details
            ref={ref}
            className={`admin-integrations-multiselect${disabled ? " admin-integrations-multiselect--disabled" : ""}`}
        >
            <summary
                className="admin-integrations-multiselect-summary"
                aria-label={t("integrations_vendor_warehouses_select")}
                onClick={(e) => { if (disabled) e.preventDefault(); }}
            >
                <Warehouse size={14} />
                <span className="admin-integrations-multiselect-summary-text">{summary}</span>
                <ChevronDown size={14} className="admin-integrations-multiselect-chevron" />
            </summary>

            <div className="admin-integrations-multiselect-menu" role="listbox" aria-multiselectable="true">
                <p className="admin-integrations-multiselect-hint">
                    {t("integrations_vendor_warehouses_hint")}
                </p>
                {options.length === 0 ? (
                    <p className="admin-integrations-multiselect-empty">
                        {t("integrations_warehouse_vendor_unset")}
                    </p>
                ) : (
                    options.map((o) => {
                        const idx = selected.indexOf(o.warehouse);
                        const isSelected = idx >= 0;
                        return (
                            <button
                                key={o.warehouse}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                className={`admin-integrations-multiselect-option${isSelected ? " admin-integrations-multiselect-option--selected" : ""}`}
                                onClick={() => toggle(o.warehouse)}
                                disabled={disabled}
                            >
                                <span className="admin-integrations-multiselect-check">
                                    {isSelected && <Check size={13} />}
                                </span>
                                <span className="admin-integrations-multiselect-label">
                                    {o.warehouse}
                                    {o.country ? ` (${o.country})` : ""}
                                </span>
                                {isSelected && (
                                    <span className="admin-integrations-multiselect-rank">
                                        {idx === 0
                                            ? t("integrations_warehouse_primary")
                                            : `#${idx + 1}`}
                                    </span>
                                )}
                            </button>
                        );
                    })
                )}
            </div>
        </details>
    );
};

export default WarehouseMultiSelect;
